import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, MapPin, Clock, Share2, Flag, Pin, Send, Trash2, AlertTriangle, EyeOff, MoreVertical, BellRing, BellOff, Eye, Pencil, Check, Loader2, CheckCircle, BarChart3, Expand, Heart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import SignUpPromptModal from '../SignUpPromptModal';
import BlogRichTextEditor from '../BlogRichTextEditor';
import { avatarThumbnail } from '../../utils/cloudinaryOptimize';
import { toast } from 'sonner';

// Auto-link URLs in plain text, rendering them as clickable links
const URL_REGEX = /(https?:\/\/[^\s<]+)/g;
const Linkify = ({ children, className }) => {
  if (typeof children !== 'string') return <span className={className}>{children}</span>;
  const parts = children.split(URL_REGEX);
  return (
    <span className={className}>
      {parts.map((part, i) =>
        URL_REGEX.test(part) ? (
          <a key={i} href={part} target={part.includes(window.location.hostname) ? '_self' : '_blank'} rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{part}</a>
        ) : part
      )}
    </span>
  );
};

const API = '/api';

const POST_TYPE_META = {
  general: { label: 'General', color: '#64748B' },
  question: { label: 'Question', color: '#6B8DE3' },
  recommendation: { label: 'Recommendation', color: '#5BAA6B' },
  poll: { label: 'Poll', color: '#C5962E' },
  photo: { label: 'Photo', color: '#9678CB' },
  'lost-found': { label: 'Lost & Found', color: '#D06060' },
  traffic: { label: 'Traffic Alert', color: '#C99430' },
  alert: { label: 'Alert', color: '#D06060' },
  'crime-safety': { label: 'Crime & Safety', color: '#A04545' },
  'parenting-family': { label: 'Parenting / Family', color: '#D08AA0' },
  'pets-animals': { label: 'Pets & Animals', color: '#9C7BCC' },
  'food-drink': { label: 'Food & Drink', color: '#C97040' },
  'home-garden': { label: 'Home & Garden', color: '#5BAA6B' },
  'events-whats-on': { label: "Events & What's On", color: '#9678CB' },
  'weather-travel': { label: 'Weather & Travel', color: '#5A9FBD' },
  'business-shoutouts': { label: 'Business Shout-outs', color: '#C5962E' },
  'for-sale-free-wanted': { label: 'For Sale / Free / Wanted', color: '#4A9E7A' },
  'local-history': { label: 'Local History', color: '#8B8178' },
  'rants-raves': { label: 'Rants & Raves', color: '#D06060' },
};

const REACTIONS = [
  { type: 'helpful', emoji: '\uD83D\uDC4D', label: 'Helpful' },
  { type: 'love', emoji: '\u2764\uFE0F', label: 'Love this' },
  { type: 'great_news', emoji: '\uD83C\uDF89', label: 'Great news' },
  { type: 'interested', emoji: '\uD83D\uDC40', label: 'Interested' },
  { type: 'thanks', emoji: '\uD83E\uDD1D', label: 'Thanks' },
];

const REACTION_EMOJI_MAP = {
  helpful: '\uD83D\uDC4D',
  love: '\u2764\uFE0F',
  great_news: '\uD83C\uDF89',
  interested: '\uD83D\uDC40',
  thanks: '\uD83E\uDD1D',
  like: '\uD83D\uDC4D',
  funny: '\uD83D\uDE02',
  interesting: '\uD83D\uDC40',
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const shortName = (fullName) => {
  if (!fullName) return 'Anonymous';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

const ReactionPicker = ({ onReact, userReaction, counts, total }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Show each reaction type with its individual count
  const reactionEntries = Object.entries(counts || {}).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
          userReaction ? 'text-[#D4A017] bg-[#D4A017]/10' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
        }`}
        data-testid="reaction-btn"
      >
        {reactionEntries.length > 0 ? (
          <span className="flex items-center gap-1">
            {reactionEntries.slice(0, 4).map(([type, count]) => (
              <span key={type} className="inline-flex items-center gap-0.5" title={REACTIONS.find(r => r.type === type)?.label}>
                <span className="text-sm">{REACTION_EMOJI_MAP[type] || '\uD83D\uDC4D'}</span>
                <span className="text-[10px]">{count}</span>
              </span>
            ))}
          </span>
        ) : userReaction ? (
          <span className="text-sm">{REACTION_EMOJI_MAP[userReaction]}</span>
        ) : (
          <span className="text-sm">{'\uD83D\uDC4D'}</span>
        )}
        {reactionEntries.length === 0 && 'React'}
      </button>
      {open && (
        <div
          className="absolute bottom-full left-0 mb-1 flex gap-0.5 bg-white rounded-full shadow-lg border border-slate-100 px-2 py-1.5 z-50"
          onMouseLeave={() => setOpen(false)}
          data-testid="reaction-picker"
        >
          {REACTIONS.map(r => (
            <button
              key={r.type}
              onClick={() => { onReact(r.type); setOpen(false); }}
              className={`flex flex-col items-center px-1.5 py-0.5 rounded-lg transition-all hover:scale-125 hover:bg-slate-50 ${
                userReaction === r.type ? 'bg-[#D4A017]/10 scale-110' : ''
              }`}
              title={r.label}
              data-testid={`react-${r.type}`}
            >
              <span className="text-lg leading-none">{r.emoji}</span>
              <span className="text-[8px] text-slate-400 mt-0.5">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CommentReactionPicker = ({ commentId, user, onShowSignUp }) => {
  const [userReaction, setUserReaction] = useState(null);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    // Auth handled by httpOnly cookie
    const fetchReactions = () => {
      axios.get(`${API}/community/comments/${commentId}/reactions`).then(r => {
        setCounts(r.data.counts || {});
        setTotal(r.data.total || 0);
        setUserReaction(r.data.user_reaction || null);
      }).catch(() => {});
    };
    fetchReactions();
    const interval = setInterval(fetchReactions, 30000);
    return () => clearInterval(interval);
  }, [commentId]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const react = async (type) => {
    if (!user) { onShowSignUp(); return; }
    try {
      const res = await axios.post(`${API}/community/comments/${commentId}/react`, { type });
      setUserReaction(res.data.type);
      const r2 = await axios.get(`${API}/community/comments/${commentId}/reactions`);
      setCounts(r2.data.counts || {});
      setTotal(r2.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setOpen(false);
  };

  // Show each reaction type with its individual count
  const reactionEntries = Object.entries(counts).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]);

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-0.5 text-[10px] transition-all rounded px-1 py-0.5 ${
          userReaction ? 'text-[#D4A017] bg-[#D4A017]/5' : 'text-slate-300 hover:text-slate-500'
        }`}
        data-testid={`comment-react-btn-${commentId}`}
      >
        {reactionEntries.length > 0 ? (
          reactionEntries.map(([type, count]) => (
            <span key={type} className="inline-flex items-center gap-px mr-0.5" title={REACTIONS.find(r => r.type === type)?.label}>
              <span className="text-xs">{REACTION_EMOJI_MAP[type]}</span>
              <span className="text-[9px]">{count}</span>
            </span>
          ))
        ) : (
          <span className="text-xs">{'\uD83D\uDC4D'}</span>
        )}
      </button>
      {open && (
        <div className="absolute bottom-full right-0 mb-1 flex gap-0.5 bg-white rounded-full shadow-lg border border-slate-100 px-1.5 py-1 z-50">
          {REACTIONS.map(r => (
            <button
              key={r.type}
              onClick={() => react(r.type)}
              className={`px-1 py-0.5 rounded transition-all hover:scale-125 ${userReaction === r.type ? 'bg-[#D4A017]/10' : ''}`}
              title={r.label}
            >
              <span className="text-sm">{r.emoji}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const EDIT_POST_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'question', label: 'Question' },
  { value: 'recommendation', label: 'Recommendation' },
  { value: 'poll', label: 'Poll' },
  { value: 'photo', label: 'Photo' },
  { value: 'lost-found', label: 'Lost & Found' },
  { value: 'traffic', label: 'Traffic Alert' },
  { value: 'alert', label: 'Local Alert' },
  { value: 'crime-safety', label: 'Crime & Safety' },
  { value: 'parenting-family', label: 'Parenting / Family' },
  { value: 'pets-animals', label: 'Pets & Animals' },
  { value: 'food-drink', label: 'Food & Drink' },
  { value: 'home-garden', label: 'Home & Garden' },
  { value: 'events-whats-on', label: "Events & What's On" },
  { value: 'weather-travel', label: 'Weather & Travel' },
  { value: 'business-shoutouts', label: 'Business Shout-outs' },
  { value: 'for-sale-free-wanted', label: 'For Sale / Free / Wanted' },
  { value: 'local-history', label: 'Local History' },
  { value: 'rants-raves', label: 'Rants & Raves' },
];

const CommunityPostCard = ({ post, onUpdate, onEdit, defaultShowComments }) => {
  const { user } = useAuth();
  const [userReaction, setUserReaction] = useState(post._user_reaction || null);
  const [reactionCounts, setReactionCounts] = useState(post._reaction_counts || {});
  const [reactionTotal, setReactionTotal] = useState(post._reaction_total || post.likes_count || 0);
  const [showComments, setShowComments] = useState(!!defaultShowComments);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [voted, setVoted] = useState(null);
  const [pollData, setPollData] = useState(post.poll);
  const [voting, setVoting] = useState(false);
  const [streak, setStreak] = useState(post._author_streak || 0);
  const [following, setFollowing] = useState(false);
  const [viewTracked, setViewTracked] = useState(false);
  const [deletePostDialog, setDeletePostDialog] = useState(false);
  const [boostVotesDialog, setBoostVotesDialog] = useState(false);
  const [boostVotes, setBoostVotes] = useState({});
  const [boostSaving, setBoostSaving] = useState(false);
  const [boostReactionsDialog, setBoostReactionsDialog] = useState(false);
  const [boostReactions, setBoostReactions] = useState({ helpful: 0, love: 0, great_news: 0, interested: 0, thanks: 0 });
  const [boostReactionsSaving, setBoostReactionsSaving] = useState(false);
  const [fakeComment, setFakeComment] = useState('');
  const [fakeCommentSaving, setFakeCommentSaving] = useState(false);
  const [boostCommentReactionsDialog, setBoostCommentReactionsDialog] = useState(null); // comment ID or null
  const [boostCommentReactions, setBoostCommentReactions] = useState({ helpful: 0, love: 0, great_news: 0, interested: 0, thanks: 0 });
  const [boostCommentReactionsSaving, setBoostCommentReactionsSaving] = useState(false);
  const [postHidden, setPostHidden] = useState(post.status === 'hidden');

  // Sync prop changes from parent's refresh cycle
  useEffect(() => { setPostHidden(post.status === 'hidden'); }, [post.status]);
  useEffect(() => { if (!voted) setPollData(post.poll); }, [post.poll]);
  useEffect(() => {
    if (post._reaction_counts) setReactionCounts(post._reaction_counts);
    if (post._reaction_total !== undefined) setReactionTotal(post._reaction_total);
    if (post._user_reaction !== undefined) setUserReaction(post._user_reaction);
  }, [post._reaction_counts, post._reaction_total, post._user_reaction]);
  useEffect(() => { if (post._author_streak !== undefined) setStreak(post._author_streak); }, [post._author_streak]);
  const [editPostDialog, setEditPostDialog] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editType, setEditType] = useState('');
  const [editTown, setEditTown] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [towns, setTowns] = useState([]);
  const typeMeta = POST_TYPE_META[post.type] || POST_TYPE_META.general;
  const isPendingOwn = post._is_pending_own || post.status === 'pending';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/towns`).then(r => setTowns(r.data?.towns || r.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isPendingOwn) return;
    // View tracking works for both logged-in and anonymous users
    if (!viewTracked) {
      const anonId = (() => {
        let id = localStorage.getItem('anon_viewer_id');
        if (!id) { id = 'anon_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('anon_viewer_id', id); }
        return id;
      })();
      // Auth handled by httpOnly cookie
      axios.post(`${API}/community/posts/${post.id}/view`, user ? {} : { anon_id: anonId }).then(() => {
        setViewTracked(true);
      }).catch(() => {});
    }
    if (!user) return;
    // Authenticated-only fetches below
    axios.get(`${API}/community/posts/${post.id}/follow-status`).then(r => {
      setFollowing(r.data.following || false);
    }).catch(() => {});
    if (post.type === 'poll' || post.also_poll) {
      axios.get(`${API}/community/posts/${post.id}/voted`)
        .then(r => { if (r.data.voted) setVoted(r.data.option_id); }).catch(() => {});
    }
  }, [post.id, post.type, post.user_id, isPendingOwn]);

  // Auto-refresh comments every 30s when expanded
  useEffect(() => {
    if (!showComments || isPendingOwn) return;
    loadComments(); // Load immediately when comments are shown
    const interval = setInterval(() => { loadComments(); }, 30000);
    return () => clearInterval(interval);
  }, [showComments, isPendingOwn, post.id]);

  const handleReact = async (type) => {
    if (!user) { setShowSignUp(true); return; }
    try {
      const res = await axios.post(`${API}/community/posts/${post.id}/react`, { type });
      setUserReaction(res.data.type);
      // Refresh counts
      const r2 = await axios.get(`${API}/community/posts/${post.id}/reactions`);
      setReactionCounts(r2.data.counts || {});
      setReactionTotal(r2.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to react');
    }
  };

  const loadComments = async () => {
    try {
      // Auth handled by httpOnly cookie
      const res = await axios.get(`${API}/community/posts/${post.id}/comments`);
      setComments(res.data.comments || []);
    } catch {}
  };

  const toggleComments = () => {
    if (!showComments) loadComments();
    setShowComments(!showComments);
  };

  const submitComment = async () => {
    if (!user) { setShowSignUp(true); return; }
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/community/posts/${post.id}/comments`, { content: newComment });
      setNewComment('');
      loadComments();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post comment');
    }
    setSubmitting(false);
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/community/comments/${commentId}`);
      loadComments();
      if (onUpdate) onUpdate();
    } catch {}
  };

  const hideComment = async (commentId) => {
    try {
      const res = await axios.post(`${API}/admin/community/comments/${commentId}/hide`, {});
      toast.success(res.data.hidden ? 'Comment hidden' : 'Comment unhidden');
      loadComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const hidePost = async () => {
    try {
      const newStatus = postHidden ? 'active' : 'hidden';
      await axios.put(`${API}/admin/community/posts/${post.id}`, { status: newStatus });
      setPostHidden(!postHidden);
      toast.success(postHidden ? 'Post unhidden' : 'Post hidden from community');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update post');
    }
  };

  const deletePost = async () => {
    try {
      await axios.delete(`${API}/admin/community/posts/${post.id}`);
      toast.success('Post deleted');
      setDeletePostDialog(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete post');
    }
  };

  const approvePost = async () => {
    try {
      await axios.post(`${API}/admin/community/posts/${post.id}/approve`, {});
      toast.success('Post approved');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const openEditPost = () => {
    setEditContent(post.content || '');
    setEditType(post.type || 'general');
    setEditTown(post.town || '');
    setEditPostDialog(true);
  };

  const openBoostVotes = () => {
    if (!pollData?.options) return;
    const initial = {};
    pollData.options.forEach(o => { initial[o.id] = 0; });
    setBoostVotes(initial);
    setBoostVotesDialog(true);
  };

  const saveBoostVotes = async () => {
    setBoostSaving(true);
    try {
      const filtered = {};
      Object.entries(boostVotes).forEach(([k, v]) => { if (v > 0) filtered[k] = v; });
      if (Object.keys(filtered).length === 0) { toast.error('Add at least 1 vote'); setBoostSaving(false); return; }
      const res = await axios.post(`${API}/admin/community/posts/${post.id}/boost-votes`, { votes: filtered });
      setPollData(res.data.poll);
      setBoostVotesDialog(false);
      toast.success(`Added ${res.data.total_added} vote${res.data.total_added !== 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to boost votes');
    }
    setBoostSaving(false);
  };

  const saveBoostReactions = async () => {
    setBoostReactionsSaving(true);
    try {
      const filtered = {};
      Object.entries(boostReactions).forEach(([k, v]) => { if (v > 0) filtered[k] = v; });
      if (Object.keys(filtered).length === 0) { toast.error('Add at least 1 reaction'); setBoostReactionsSaving(false); return; }
      const res = await axios.post(`${API}/admin/community/posts/${post.id}/boost-reactions`, { reactions: filtered });
      setBoostReactionsDialog(false);
      setBoostReactions({ helpful: 0, love: 0, great_news: 0, interested: 0, thanks: 0 });
      toast.success(`Added ${res.data.total_added} reaction${res.data.total_added !== 1 ? 's' : ''} with notifications`);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to boost reactions');
    }
    setBoostReactionsSaving(false);
  };

  const submitFakeComment = async () => {
    if (!fakeComment.trim()) return;
    setFakeCommentSaving(true);
    try {
      await axios.post(`${API}/admin/community/posts/${post.id}/fake-comment`, { content: fakeComment });
      setFakeComment('');
      toast.success('Comment posted as random user (with notifications)');
      loadComments();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post fake comment');
    }
    setFakeCommentSaving(false);
  };

  const saveBoostCommentReactions = async () => {
    if (!boostCommentReactionsDialog) return;
    setBoostCommentReactionsSaving(true);
    try {
      const filtered = {};
      Object.entries(boostCommentReactions).forEach(([k, v]) => { if (v > 0) filtered[k] = v; });
      if (Object.keys(filtered).length === 0) { toast.error('Add at least 1 reaction'); setBoostCommentReactionsSaving(false); return; }
      const res = await axios.post(`${API}/admin/community/comments/${boostCommentReactionsDialog}/boost-reactions`, { reactions: filtered });
      setBoostCommentReactionsDialog(null);
      setBoostCommentReactions({ helpful: 0, love: 0, great_news: 0, interested: 0, thanks: 0 });
      toast.success(`Added ${res.data.total_added} reaction${res.data.total_added !== 1 ? 's' : ''} to comment`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to boost comment reactions');
    }
    setBoostCommentReactionsSaving(false);
  };

  const saveEditPost = async () => {
    setEditSaving(true);
    try {
      await axios.put(`${API}/admin/community/posts/${post.id}`, {
        content: editContent,
        type: editType,
        town: editTown,
      });
      toast.success('Post updated');
      setEditPostDialog(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setEditSaving(false);
  };

  const votePoll = async (optionId) => {
    if (!user) { setShowSignUp(true); return; }
    if (voted || pollClosed) return;
    setVoting(true);
    try {
      const res = await axios.post(`${API}/community/posts/${post.id}/vote`, { option_id: optionId });
      setVoted(optionId);
      setPollData(res.data.poll);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to vote');
    }
    setVoting(false);
  };

  // Poll expiry: compute whether closed and remaining time string
  const pollExpiresAt = pollData?.expires_at
    || (post.created_at ? new Date(new Date(post.created_at).getTime() + 7 * 24 * 3600 * 1000).toISOString() : null);
  const pollClosed = pollExpiresAt ? new Date() > new Date(pollExpiresAt) : false;
  const pollTimeLeft = (() => {
    if (!pollExpiresAt) return null;
    const diff = new Date(pollExpiresAt) - new Date();
    if (diff <= 0) return null;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h ${mins}m left`;
    return `${mins}m left`;
  })();

  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reporting, setReporting] = useState(false);

  const reportPost = async () => {
    if (!user) { setShowSignUp(true); return; }
    setShowReportDialog(true);
  };

  const submitReport = async () => {
    if (!reportReason.trim()) { toast.error('Please provide a reason.'); return; }
    setReporting(true);
    try {
      await axios.post(`${API}/community/posts/${post.id}/report`, { reason: reportReason });
      toast.success('Thank you. Our team will review this post.');
      setShowReportDialog(false);
      setReportReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to report post');
    }
    setReporting(false);
  };

  const sharePost = async () => {
    const url = `${window.location.origin}/community/post/${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title || post.content?.slice(0, 60) || 'Community Post', url });
        return;
      }
    } catch (e) {
      if (e.name !== 'AbortError') { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Link copied to clipboard!');
    }
  };

  const toggleFollow = async () => {
    if (!user) { setShowSignUp(true); return; }
    try {
      if (following) {
        await axios.delete(`${API}/community/posts/${post.id}/follow`);
        setFollowing(false);
        toast.success('Unfollowed post');
      } else {
        await axios.post(`${API}/community/posts/${post.id}/follow`, {});
        setFollowing(true);
        toast.success('Following post — you\'ll be notified of new activity');
      }
    } catch {
      toast.error('Failed to update follow status');
    }
  };

  return (
    <article
      className={`rounded-xl border overflow-hidden transition-all ${
        isPendingOwn
          ? 'bg-slate-50/80 border-dashed border-slate-200 opacity-70'
          : `bg-white border-slate-100 hover:shadow-sm ${post.pinned ? 'ring-2 ring-[#D4A017]/20 border-[#D4A017]/30' : 'hover:border-slate-200'}`
      }`}
      data-testid={`community-post-${post.id}`}
    >
      {isPendingOwn && (
        <div className="px-5 py-1.5 bg-amber-50 border-b border-amber-100 flex items-center gap-1.5" data-testid="post-pending-badge">
          <Clock className="h-3 w-3 text-amber-500" />
          <span className="text-[11px] font-medium text-amber-600">Awaiting approval — only you can see this</span>
        </div>
      )}
      {post.pinned && (
        <div className="px-5 py-1.5 bg-[#D4A017]/5 border-b border-[#D4A017]/10 flex items-center gap-1.5">
          <Pin className="h-3 w-3 text-[#D4A017]" />
          <span className="text-[11px] font-medium text-[#D4A017]">Pinned by moderator</span>
        </div>
      )}

      {/* Header */}
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0F2C3E] to-[#1a4a6b] flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
            {post.user_image ? (
              <img src={avatarThumbnail(post.user_image, 80)} alt={`${post.user_name || 'User'} profile photo`} loading="lazy" className="w-full h-full object-cover" />
            ) : (
              <span className="text-sm font-bold text-white">{((post.display_name || post.user_name || 'A'))[0].toUpperCase()}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-[#0F2C3E]">{post.display_name || shortName(post.user_name)}</span>
              {post.display_name === 'Discover Essex Team' && (
                <span className="inline-flex items-center justify-center ml-1 w-4 h-4 bg-blue-500 rounded-full align-middle" title="Verified by Discover Essex" data-testid="admin-verified-badge">
                  <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                </span>
              )}
              {streak >= 2 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded-full" data-testid="streak-badge" title={`${streak}-day activity streak`}>
                  {'\uD83D\uDD25'} {streak}d
                </span>
              )}
              <Badge
                className="text-[10px] py-0 border"
                style={{ backgroundColor: `${typeMeta.color}10`, color: typeMeta.color, borderColor: `${typeMeta.color}25` }}
              >
                {typeMeta.label}
              </Badge>
              {post.also_poll && post.type !== 'poll' && (
                <Badge
                  className="text-[10px] py-0 border"
                  style={{ backgroundColor: '#C5962E10', color: '#C5962E', borderColor: '#C5962E25' }}
                >
                  Poll
                </Badge>
              )}
              {postHidden && isAdmin && (
                <Badge variant="outline" className="text-[10px] py-0 text-amber-600 border-amber-300 bg-amber-50">Hidden</Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
              <Link to={`/town/${post.town?.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-center gap-1 hover:text-[#D4A017] transition-colors">
                <MapPin className="h-3 w-3" />{post.town}
              </Link>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />{timeAgo(post.created_at)}
              </span>
              {isAdmin && (
                <span className="flex items-center gap-1 text-indigo-400" data-testid="post-view-count">
                  <Eye className="h-3 w-3" />{post.unique_views || 0} {(post.unique_views || 0) === 1 ? 'view' : 'views'}
                </span>
              )}
            </div>
          </div>
          {/* Admin post actions */}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1.5 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-testid={`post-admin-menu-${post.id}`}>
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px]">
                {post.status === 'pending' && (
                  <DropdownMenuItem onClick={approvePost} className="text-green-600" data-testid={`approve-post-${post.id}`}>
                    <CheckCircle className="h-3.5 w-3.5 mr-2" />Approve Post
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={openEditPost} data-testid={`edit-post-admin-${post.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-2" />Edit Post
                </DropdownMenuItem>
                {(post.type === 'poll' || post.also_poll) && pollData && (
                  <DropdownMenuItem onClick={openBoostVotes} data-testid={`boost-votes-${post.id}`}>
                    <BarChart3 className="h-3.5 w-3.5 mr-2" />Boost Poll Votes
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setBoostReactionsDialog(true)} data-testid={`boost-reactions-${post.id}`}>
                  <Heart className="h-3.5 w-3.5 mr-2" />Boost Reactions
                </DropdownMenuItem>
                <DropdownMenuItem onClick={hidePost} data-testid={`hide-post-${post.id}`}>
                  <EyeOff className="h-3.5 w-3.5 mr-2" />{postHidden ? 'Unhide Post' : 'Hide Post'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setDeletePostDialog(true)} className="text-red-600" data-testid={`delete-post-${post.id}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-2" />Delete Post
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Content — render as rich text if it contains HTML tags */}
        {post.content && post.content.includes('<') ? (
          <div className="text-sm text-slate-700 leading-relaxed mt-3 prose prose-sm max-w-none prose-p:my-1 prose-headings:my-2" data-testid="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <p className="text-sm text-slate-700 leading-relaxed mt-3 whitespace-pre-wrap" data-testid="post-content"><Linkify>{post.content}</Linkify></p>
        )}

        {/* Pending edit indicator */}
        {post.has_pending_edit && user && post.user_id === user.id && (
          <div className="mt-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-100 flex items-center gap-1.5" data-testid="pending-edit-badge">
            <Pencil className="h-3 w-3 text-blue-500" />
            <span className="text-[11px] font-medium text-blue-600">You have a pending edit awaiting admin approval</span>
          </div>
        )}

        {post.images && post.images.length > 0 && (
          <div className={`mt-3 grid gap-2 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`} data-testid="post-images">
            {post.images.map((img, i) => {
              // Apply Cloudinary auto-quality/format transformations for sharper images
              const optimized = img.includes('res.cloudinary.com') && img.includes('/upload/')
                ? img.replace('/upload/', '/upload/w_1200,q_auto,f_auto/')
                : img;
              return (
                <img key={i} src={optimized} alt={`Post image ${i + 1}`} className="rounded-lg w-full object-cover max-h-96" loading="lazy" />
              );
            })}
          </div>
        )}

        {/* Poll */}
        {(post.type === 'poll' || post.also_poll) && pollData && (
          <div className="mt-4 space-y-2" data-testid="post-poll">
            {(voted !== null || pollClosed
              ? [...pollData.options].sort((a, b) => b.votes - a.votes)
              : pollData.options
            ).map(opt => {
              const pct = pollData.total_votes > 0 ? Math.round((opt.votes / pollData.total_votes) * 100) : 0;
              const isSelected = voted === opt.id;
              const showResults = voted !== null || pollClosed;
              return (
                <button
                  key={opt.id}
                  onClick={() => votePoll(opt.id)}
                  disabled={voted || voting || pollClosed}
                  className={`w-full text-left relative rounded-lg border overflow-hidden transition-all ${
                    isSelected ? 'border-[#D4A017] bg-[#D4A017]/5 shadow-sm'
                      : voted || pollClosed ? 'border-slate-100 bg-slate-50/50'
                        : 'border-slate-200 hover:border-[#D4A017] hover:bg-[#D4A017]/5 cursor-pointer'
                  }`}
                  data-testid={`poll-option-${opt.id}`}
                >
                  {showResults && <div className="absolute inset-y-0 left-0 bg-[#D4A017]/8 transition-all duration-500 ease-out" style={{ width: `${pct}%` }} />}
                  <div className="relative flex items-center justify-between px-4 py-3">
                    <span className={`text-sm ${isSelected ? 'font-semibold text-[#0F2C3E]' : 'font-medium text-slate-700'}`}>{opt.text}</span>
                    {showResults && <span className={`text-xs font-bold ${isSelected ? 'text-[#D4A017]' : 'text-slate-400'}`}>{pct}% <span className="font-normal">({opt.votes})</span></span>}
                  </div>
                </button>
              );
            })}
            <div className="flex items-center justify-between pt-1">
              <p className="text-xs text-slate-400">
                {pollData.total_votes} vote{pollData.total_votes !== 1 ? 's' : ''}
                {!voted && !user && !pollClosed && <span className="text-[#D4A017] ml-1 cursor-pointer" onClick={() => setShowSignUp(true)}>&middot; Sign up to vote</span>}
              </p>
              {pollClosed ? (
                <span className="text-xs font-medium text-slate-400">Poll closed</span>
              ) : pollTimeLeft ? (
                <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" />{pollTimeLeft}</span>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* Actions — hidden for pending posts */}
      {!isPendingOwn && (
      <div className="px-3 sm:px-5 py-2 sm:py-2.5 border-t border-slate-50 flex items-center gap-0.5 sm:gap-1">
        <ReactionPicker onReact={handleReact} userReaction={userReaction} counts={reactionCounts} total={reactionTotal} />
        <button
          onClick={toggleComments}
          className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            showComments ? 'text-[#0F2C3E] bg-slate-100' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
          }`}
          data-testid="comment-btn"
        >
          <MessageSquare className="h-3.5 w-3.5 shrink-0" />
          {post.comments_count > 0 ? (
            <span>{post.comments_count} <span className="hidden sm:inline">Comment{post.comments_count !== 1 ? 's' : ''}</span></span>
          ) : (
            <span className="hidden sm:inline">Comment</span>
          )}
        </button>
        <button
          onClick={sharePost}
          className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all"
          data-testid="share-btn"
        >
          <Share2 className="h-3.5 w-3.5 shrink-0" /> <span className="hidden sm:inline">Share</span>
        </button>
        <Link
          to={`/community/post/${post.id}`}
          className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all"
          data-testid="view-post-btn"
          title="View full post"
        >
          <Expand className="h-3.5 w-3.5 shrink-0" /> <span className="hidden sm:inline">View</span>
        </Link>
        <button
          onClick={toggleFollow}
          className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            following ? 'text-[#D4A017] bg-[#D4A017]/10 hover:bg-[#D4A017]/15' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
          }`}
          data-testid="follow-btn"
          title={following ? 'Unfollow — stop notifications for this post' : 'Follow — get notified of new activity'}
        >
          {following ? <BellOff className="h-3.5 w-3.5 shrink-0" /> : <BellRing className="h-3.5 w-3.5 shrink-0" />}
          <span className="hidden sm:inline">{following ? 'Following' : 'Follow'}</span>
        </button>
        <button
          onClick={reportPost}
          className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium text-slate-300 hover:bg-red-50 hover:text-red-500 transition-all ml-auto"
          data-testid="report-btn"
          title="Report this post"
        >
          <Flag className="h-3.5 w-3.5 shrink-0" />
        </button>
        {user && post.user_id === user.id && !post.has_pending_edit && (
          <button
            onClick={() => onEdit && onEdit(post)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:bg-slate-50 hover:text-slate-700 transition-all"
            data-testid="edit-post-btn"
            title="Edit this post"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      )}

      {/* Comments */}
      {showComments && !isPendingOwn && (
        <div className="px-5 pb-4 border-t border-slate-50" data-testid="comments-section">
          <div className="space-y-3 mt-3">
            {comments.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-2">No comments yet. Be the first!</p>
            )}
            {comments.map(c => (
              <div key={c.id} className={`flex gap-2 ${c.hidden ? 'opacity-50' : ''}`} data-testid={`comment-${c.id}`}>
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center shrink-0 text-xs font-bold text-slate-600">
                  {(c.display_name || c.user_name || 'A')[0].toUpperCase()}
                </div>
                <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-[#0F2C3E]">{c.display_name || shortName(c.user_name)}</span>
                      {c.display_name === 'Discover Essex Team' && (
                        <span className="inline-flex items-center justify-center ml-1 w-3.5 h-3.5 bg-blue-500 rounded-full align-middle" title="Verified by Discover Essex" data-testid="admin-comment-verified-badge">
                          <svg className="h-2 w-2 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        </span>
                      )}
                      {c.hidden && isAdmin && (
                        <Badge variant="outline" className="text-[9px] py-0 text-orange-500 border-orange-200">Hidden</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-slate-400">{timeAgo(c.created_at)}</span>
                      <CommentReactionPicker commentId={c.id} user={user} onShowSignUp={() => setShowSignUp(true)} />
                      {/* Admin actions */}
                      {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="text-slate-300 hover:text-slate-500 transition-colors p-0.5" data-testid={`comment-admin-menu-${c.id}`}>
                              <MoreVertical className="h-3 w-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="text-xs">
                            <DropdownMenuItem onClick={() => { setBoostCommentReactions({ helpful: 0, love: 0, great_news: 0, interested: 0, thanks: 0 }); setBoostCommentReactionsDialog(c.id); }} data-testid={`boost-comment-reactions-${c.id}`}>
                              <Heart className="h-3 w-3 mr-2" />Boost Reactions
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => hideComment(c.id)} data-testid={`hide-comment-${c.id}`}>
                              <EyeOff className="h-3 w-3 mr-2" />{c.hidden ? 'Unhide' : 'Hide'} comment
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteComment(c.id)} className="text-red-600" data-testid={`delete-comment-${c.id}`}>
                              <Trash2 className="h-3 w-3 mr-2" />Delete comment
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {/* Owner delete */}
                      {user && c.user_id === user.id && !isAdmin && (
                        <button onClick={() => deleteComment(c.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap"><Linkify>{c.content}</Linkify></p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <input
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submitComment()}
              placeholder={user ? "Write a comment..." : "Sign up to comment"}
              className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#D4A017] focus:ring-1 focus:ring-[#D4A017]/20 transition-all"
              disabled={!user}
              data-testid="comment-input"
            />
            <Button size="sm" onClick={submitComment} disabled={submitting || !newComment.trim()} className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90" data-testid="submit-comment">
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
          {/* Admin: Post comment as random user */}
          {isAdmin && (
            <div className="flex gap-2 mt-2" data-testid="fake-comment-section">
              <input
                value={fakeComment}
                onChange={e => setFakeComment(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submitFakeComment()}
                placeholder="Post as random user..."
                className="flex-1 text-xs border border-dashed border-amber-300 bg-amber-50/50 rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#D4A017] focus:ring-1 focus:ring-[#D4A017]/20 transition-all placeholder:text-amber-400"
                data-testid="fake-comment-input"
              />
              <Button size="sm" variant="outline" onClick={submitFakeComment} disabled={fakeCommentSaving || !fakeComment.trim()} className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="submit-fake-comment">
                {fakeCommentSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              </Button>
            </div>
          )}
        </div>
      )}

      <SignUpPromptModal isOpen={showSignUp} onClose={() => setShowSignUp(false)} variant="community" />

      {/* Report dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" /> Report Post
            </DialogTitle>
            <DialogDescription>
              Tell us why you think this post should be reviewed by our moderation team.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reportReason}
            onChange={e => setReportReason(e.target.value)}
            placeholder="e.g. Spam, offensive content, misleading information..."
            rows={3}
            className="text-sm"
            data-testid="report-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setShowReportDialog(false); setReportReason(''); }}>Cancel</Button>
            <Button size="sm" onClick={submitReport} disabled={reporting || !reportReason.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="submit-report-btn">
              {reporting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin delete post confirmation */}
      <Dialog open={deletePostDialog} onOpenChange={setDeletePostDialog}>
        <DialogContent className="max-w-sm" data-testid="delete-post-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Trash2 className="h-4 w-4 text-red-500" /> Delete Post?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete this post and all its comments and reactions. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeletePostDialog(false)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={deletePost} data-testid="confirm-delete-post">
              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin edit post dialog */}
      <Dialog open={editPostDialog} onOpenChange={setEditPostDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-edit-post-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Pencil className="h-4 w-4 text-[#D4A017]" /> Edit Post
            </DialogTitle>
            <DialogDescription>
              Modify this post's content, category, or location.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-2">
              Post by <strong>{post.display_name || shortName(post.user_name)}</strong> &middot; {timeAgo(post.created_at)}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Category</label>
                <Select value={editType} onValueChange={setEditType}>
                  <SelectTrigger className="h-9 text-xs" data-testid="edit-post-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EDIT_POST_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Town</label>
                <Select value={editTown} onValueChange={setEditTown}>
                  <SelectTrigger className="h-9 text-xs" data-testid="edit-post-town-select">
                    <SelectValue placeholder="Select town" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Essex Wide">Essex Wide</SelectItem>
                    {towns.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Content</label>
              <BlogRichTextEditor
                value={editContent}
                onChange={setEditContent}
                placeholder="Edit post content..."
                className="community-edit-editor"
                mode="compact"
                data-testid="edit-post-content-textarea"
              />
              <style>{`.community-edit-editor .tiptap { min-height: 120px !important; } .community-edit-editor { font-size: 14px; }`}</style>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setEditPostDialog(false)} data-testid="edit-post-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={saveEditPost} disabled={editSaving || !editContent.trim()} className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90" data-testid="edit-post-save-btn">
              {editSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin boost poll votes dialog */}
      <Dialog open={boostVotesDialog} onOpenChange={setBoostVotesDialog}>
        <DialogContent className="max-w-sm" data-testid="boost-votes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-[#D4A017]" /> Boost Poll Votes
            </DialogTitle>
            <DialogDescription>
              Add votes to each poll option. These are anonymous and cannot be traced.
            </DialogDescription>
          </DialogHeader>
          {pollData?.options && (
            <div className="space-y-3 mt-2">
              {pollData.options.map(opt => (
                <div key={opt.id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{opt.text}</p>
                    <p className="text-[10px] text-slate-400">{opt.votes} current votes</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setBoostVotes(prev => ({ ...prev, [opt.id]: Math.max(0, (prev[opt.id] || 0) - 1) }))}
                      className="w-7 h-7 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 text-sm font-bold"
                    >-</button>
                    <input
                      type="number"
                      min="0"
                      value={boostVotes[opt.id] || 0}
                      onChange={e => setBoostVotes(prev => ({ ...prev, [opt.id]: Math.max(0, parseInt(e.target.value) || 0) }))}
                      className="w-12 h-7 text-center text-xs rounded-md border border-slate-200 font-medium"
                      data-testid={`boost-input-${opt.id}`}
                    />
                    <button
                      onClick={() => setBoostVotes(prev => ({ ...prev, [opt.id]: (prev[opt.id] || 0) + 1 }))}
                      className="w-7 h-7 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 text-sm font-bold"
                    >+</button>
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-slate-400 text-right">
                Adding {Object.values(boostVotes).reduce((a, b) => a + b, 0)} vote{Object.values(boostVotes).reduce((a, b) => a + b, 0) !== 1 ? 's' : ''} total
              </p>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" size="sm" onClick={() => setBoostVotesDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={saveBoostVotes} disabled={boostSaving} className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90" data-testid="confirm-boost-votes">
              {boostSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <BarChart3 className="h-3.5 w-3.5 mr-1" />}
              Add Votes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin boost reactions dialog */}
      <Dialog open={boostReactionsDialog} onOpenChange={setBoostReactionsDialog}>
        <DialogContent className="max-w-sm" data-testid="boost-reactions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Heart className="h-4 w-4 text-[#D4A017]" /> Boost Reactions
            </DialogTitle>
            <DialogDescription>
              Add reactions from random users. Each will send a notification to the post author with a realistic name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {[
              { key: 'helpful', emoji: '\uD83D\uDC4D', label: 'Helpful' },
              { key: 'love', emoji: '\u2764\uFE0F', label: 'Love' },
              { key: 'great_news', emoji: '\uD83C\uDF89', label: 'Great News' },
              { key: 'interested', emoji: '\uD83D\uDC40', label: 'Interested' },
              { key: 'thanks', emoji: '\uD83E\uDD1D', label: 'Thanks' },
            ].map(({ key, emoji, label }) => (
              <div key={key} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-700">{emoji} {label}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setBoostReactions(prev => ({ ...prev, [key]: Math.max(0, prev[key] - 1) }))}
                    className="w-7 h-7 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 text-sm font-bold"
                  >-</button>
                  <input
                    type="number"
                    min="0"
                    value={boostReactions[key]}
                    onChange={e => setBoostReactions(prev => ({ ...prev, [key]: Math.max(0, parseInt(e.target.value) || 0) }))}
                    className="w-12 h-7 text-center text-xs rounded-md border border-slate-200 font-medium"
                    data-testid={`boost-reaction-input-${key}`}
                  />
                  <button
                    onClick={() => setBoostReactions(prev => ({ ...prev, [key]: prev[key] + 1 }))}
                    className="w-7 h-7 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 text-sm font-bold"
                  >+</button>
                </div>
              </div>
            ))}
            <p className="text-[10px] text-slate-400 text-right">
              Adding {Object.values(boostReactions).reduce((a, b) => a + b, 0)} reaction{Object.values(boostReactions).reduce((a, b) => a + b, 0) !== 1 ? 's' : ''} total
            </p>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" size="sm" onClick={() => setBoostReactionsDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={saveBoostReactions} disabled={boostReactionsSaving} className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90" data-testid="confirm-boost-reactions">
              {boostReactionsSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Heart className="h-3.5 w-3.5 mr-1" />}
              Add Reactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Admin boost comment reactions dialog */}
      <Dialog open={!!boostCommentReactionsDialog} onOpenChange={(open) => { if (!open) setBoostCommentReactionsDialog(null); }}>
        <DialogContent className="max-w-sm" data-testid="boost-comment-reactions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Heart className="h-4 w-4 text-[#D4A017]" /> Boost Comment Reactions
            </DialogTitle>
            <DialogDescription>
              Add reactions to this comment from random users with notifications.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {[
              { key: 'helpful', emoji: '\uD83D\uDC4D', label: 'Helpful' },
              { key: 'love', emoji: '\u2764\uFE0F', label: 'Love' },
              { key: 'great_news', emoji: '\uD83C\uDF89', label: 'Great News' },
              { key: 'interested', emoji: '\uD83D\uDC40', label: 'Interested' },
              { key: 'thanks', emoji: '\uD83E\uDD1D', label: 'Thanks' },
            ].map(({ key, emoji, label }) => (
              <div key={key} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-700">{emoji} {label}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setBoostCommentReactions(prev => ({ ...prev, [key]: Math.max(0, prev[key] - 1) }))} className="w-7 h-7 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 text-sm font-bold">-</button>
                  <input type="number" min="0" value={boostCommentReactions[key]} onChange={e => setBoostCommentReactions(prev => ({ ...prev, [key]: Math.max(0, parseInt(e.target.value) || 0) }))} className="w-12 h-7 text-center text-xs rounded-md border border-slate-200 font-medium" data-testid={`boost-comment-reaction-input-${key}`} />
                  <button onClick={() => setBoostCommentReactions(prev => ({ ...prev, [key]: prev[key] + 1 }))} className="w-7 h-7 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 text-sm font-bold">+</button>
                </div>
              </div>
            ))}
            <p className="text-[10px] text-slate-400 text-right">Adding {Object.values(boostCommentReactions).reduce((a, b) => a + b, 0)} total</p>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" size="sm" onClick={() => setBoostCommentReactionsDialog(null)}>Cancel</Button>
            <Button size="sm" onClick={saveBoostCommentReactions} disabled={boostCommentReactionsSaving} className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90" data-testid="confirm-boost-comment-reactions">
              {boostCommentReactionsSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Heart className="h-3.5 w-3.5 mr-1" />}
              Add Reactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </article>
  );
};

export default CommunityPostCard;
